import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getContractorById, 
  getContractorWorkers, 
  getContractorOrders,
  updateContractor,
  getContractorServiceableJobs,
  addContractorServiceableJob,
  addContractorServiceableJobsBulk,
  deleteContractorServiceableJob,
  getAdminServices,
  getAdminCategories,
  getAdminSubCategories,
  getServiceableCities
} from '../Config/contractorService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Topbar from '../Components/Topbar';
import { formatEasternDate } from '../Utils/dateTime';

function ContractorDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contractor, setContractor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [workers, setWorkers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderStatus, setOrderStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showServiceableJobModal, setShowServiceableJobModal] = useState(false);
  const [loadingServiceableJobs, setLoadingServiceableJobs] = useState(false);
  const [serviceableJobs, setServiceableJobs] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [subCategoriesLoaded, setSubCategoriesLoaded] = useState(false);
  const [serviceableCities, setServiceableCities] = useState([]);
  const [loadingAddServiceableJobMeta, setLoadingAddServiceableJobMeta] = useState(false);
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const [activeAddStep, setActiveAddStep] = useState('category');
  const [groupByMode, setGroupByMode] = useState('service');
  const [expandedServiceGroups, setExpandedServiceGroups] = useState({});
  const [expandedPincodeGroups, setExpandedPincodeGroups] = useState({});
  const [serviceableJobFormData, setServiceableJobFormData] = useState({
    category_ids: [],
    city_values: [],
    service_ids: []
  });
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    gender: '',
    profile_photo: '',
    age: '',
    active: true,
    commission_percentage: ''
  });

  useEffect(() => {
    const fetchContractorDetails = async () => {
      try {
        const response = await getContractorById(id);
        if (response.status === 'success') {
          setContractor(response.data);
        } else {
          toast.error('Failed to fetch contractor details');
          navigate('/contractors');
        }
      } catch (error) {
        toast.error('Failed to fetch contractor details');
        navigate('/contractors');
      }
      setLoading(false);
    };

    fetchContractorDetails();
  }, [id, navigate]);

  const fetchServiceableJobs = useCallback(async () => {
    setLoadingServiceableJobs(true);
    try {
      const response = await getContractorServiceableJobs(id);
      if (response.status === 'success') {
        const jobs = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setServiceableJobs(jobs);
      } else {
        toast.error(response.message || 'Failed to fetch serviceable jobs');
      }
    } catch (error) {
      toast.error('Failed to fetch serviceable jobs');
    }
    setLoadingServiceableJobs(false);
  }, [id]);

  useEffect(() => {
    if (activeTab === 'serviceable-jobs') {
      fetchServiceableJobs();
    }
  }, [activeTab, fetchServiceableJobs]);

  const fetchOrders = async (status = null) => {
    setLoadingOrders(true);
    try {
      const response = await getContractorOrders(id, status);
      if (response.status === 'success') {
        setOrders(response.data);
      }
    } catch (error) {
      toast.error('Failed to fetch orders');
    }
    setLoadingOrders(false);
  };

  const handleOrderStatusChange = async (e) => {
    const status = e.target.value;
    setOrderStatus(status);
    await fetchOrders(status);
  };

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    if (tab === 'workers') {
      setLoadingWorkers(true);
      try {
        const response = await getContractorWorkers(id);
        if (response.status === 'success') {
          setWorkers(response.data);
        }
      } catch (error) {
        toast.error('Failed to fetch workers');
      }
      setLoadingWorkers(false);
    } else if (tab === 'orders') {
      await fetchOrders(orderStatus);
    } else if (tab === 'serviceable-jobs') {
      await fetchServiceableJobs();
    }
  };

  const fetchServices = async () => {
    try {
      const response = await getAdminServices();
      const serviceList = Array.isArray(response)
        ? response
        : Array.isArray(response.data)
          ? response.data
          : [];
      setServices(serviceList);
    } catch (error) {
      toast.error('Failed to load services');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getAdminCategories();
      const list = Array.isArray(response)
        ? response
        : Array.isArray(response.data)
          ? response.data
          : [];
      setCategories(list);
    } catch (error) {
      toast.error('Failed to load categories');
    }
  };

  const fetchServiceableCities = async () => {
    try {
      const response = await getServiceableCities();
      const list = Array.isArray(response)
        ? response
        : Array.isArray(response.data)
          ? response.data
          : [];
      setServiceableCities(list.filter((city) => city?.active !== false));
    } catch (error) {
      toast.error('Failed to load serviceable cities');
    }
  };

  const fetchSubCategories = async () => {
    try {
      const response = await getAdminSubCategories();
      const list = Array.isArray(response)
        ? response
        : Array.isArray(response.data)
          ? response.data
          : [];
      setSubCategories(list);
      setSubCategoriesLoaded(true);
    } catch (error) {
      toast.error('Failed to load sub categories');
    }
  };

  const handleEdit = () => {
    setFormData({
      name: contractor.user.name,
      mobile: contractor.user.mobile,
      email: contractor.user.email,
      gender: contractor.user.gender,
      profile_photo: contractor.user.profile_photo,
      age: contractor.user.age,
      active: contractor.active,
      commission_percentage: contractor.commission_percentage
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await updateContractor(id, formData);
      if (response.status === 'success') {
        toast.success('Contractor updated successfully');
        setShowModal(false);
        // Refresh contractor details
        const updatedContractor = await getContractorById(id);
        if (updatedContractor.status === 'success') {
          setContractor(updatedContractor.data);
        }
      } else {
        toast.error('Failed to update contractor');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const normalizePincode = (value) => String(value || '').trim();
  const normalizeId = (value) => String(value ?? '').trim();
  const getCategoryIdValue = (category) => normalizeId(category?.category_id ?? category?.id ?? '');

  const groupedServiceableJobs = useMemo(() => {
    const groups = {};
    serviceableJobs.forEach((job) => {
      const serviceId = job.service_id || 'unknown-service';
      if (!groups[serviceId]) {
        groups[serviceId] = {
          serviceId,
          serviceTitle: job.service?.title || 'Unknown Service',
          categoryName: job.category?.name || job.category?.title || '-',
          subCategoryName: job.subCategory?.name || job.subCategory?.title || '-',
          jobs: []
        };
      }
      groups[serviceId].jobs.push(job);
    });

    return Object.values(groups)
      .map((group) => ({
        ...group,
        jobs: [...group.jobs].sort((a, b) => String(a.pincode || '').localeCompare(String(b.pincode || '')))
      }))
      .sort((a, b) => a.serviceTitle.localeCompare(b.serviceTitle));
  }, [serviceableJobs]);

  const filteredCategories = useMemo(() => {
    const query = categorySearchTerm.trim().toLowerCase();
    if (!query) return categories;
    return categories.filter((category) =>
      String(category.title || category.name || '').toLowerCase().includes(query)
    );
  }, [categories, categorySearchTerm]);

  const filteredServicesByCategory = useMemo(() => {
    const selectedCategoryIds = new Set((serviceableJobFormData.category_ids || []).map((idValue) => normalizeId(idValue)));
    if (!selectedCategoryIds.size) return services;

    const subCategoryToCategory = new Map();
    subCategories.forEach((subCategory) => {
      const subCategoryId = normalizeId(subCategory.sub_category_id || subCategory.id);
      const categoryId = normalizeId(subCategory.category_id || subCategory.category?.category_id || subCategory.category?.id);
      if (subCategoryId && categoryId) {
        subCategoryToCategory.set(subCategoryId, categoryId);
      }
    });

    const resolveServiceCategoryId = (service) =>
      normalizeId(
        service.category_id ??
        service.categoryId ??
        service.category?.category_id ??
        service.category?.id ??
        service.sub_category?.category_id ??
        service.subCategory?.category_id ??
        service.subCategory?.categoryId ??
        subCategoryToCategory.get(normalizeId(service.sub_category_id ?? service.subCategoryId ?? service.sub_category?.sub_category_id)) ??
        ''
      );

    return services.filter((service) => {
      const serviceCategoryId = resolveServiceCategoryId(service);
      return selectedCategoryIds.has(serviceCategoryId);
    });
  }, [services, subCategories, serviceableJobFormData.category_ids]);

  useEffect(() => {
    if (
      showServiceableJobModal &&
      serviceableJobFormData.category_ids.length > 0 &&
      !subCategoriesLoaded
    ) {
      fetchSubCategories();
    }
  }, [showServiceableJobModal, serviceableJobFormData.category_ids, subCategoriesLoaded]);

  const selectedCityValues = useMemo(() => serviceableJobFormData.city_values, [serviceableJobFormData.city_values]);

  const existingServiceCityPairs = useMemo(() => {
    const set = new Set();
    serviceableJobs.forEach((job) => {
      set.add(`${job.service_id}::${String(job.pincode || '')}`);
    });
    return set;
  }, [serviceableJobs]);

  const filteredCities = useMemo(() => {
    const query = citySearchTerm.trim().toLowerCase();
    if (!query) return serviceableCities;
    return serviceableCities.filter((city) => String(city.city || '').toLowerCase().includes(query));
  }, [serviceableCities, citySearchTerm]);

  const availableCitiesToAdd = useMemo(() => {
    if (!serviceableJobFormData.service_ids.length) {
      return filteredCities;
    }
    return filteredCities.filter((city) => {
      const cityValue = String(city.city || '');
      return serviceableJobFormData.service_ids.some(
        (serviceId) => !existingServiceCityPairs.has(`${serviceId}::${cityValue}`)
      );
    });
  }, [filteredCities, serviceableJobFormData.service_ids, existingServiceCityPairs]);

  const filteredServicesWithSearch = useMemo(() => {
    const query = serviceSearchTerm.trim().toLowerCase();
    if (!query) return filteredServicesByCategory;
    return filteredServicesByCategory.filter((service) => String(service.title || '').toLowerCase().includes(query));
  }, [filteredServicesByCategory, serviceSearchTerm]);

  const availableServicesToAdd = useMemo(() => {
    return filteredServicesWithSearch;
  }, [filteredServicesWithSearch]);

  useEffect(() => {
    setServiceableJobFormData((prev) => {
      const allowed = new Set(availableServicesToAdd.map((service) => service.service_id));
      const filteredSelected = prev.service_ids.filter((serviceId) => allowed.has(serviceId));
      if (filteredSelected.length === prev.service_ids.length) {
        return prev;
      }
      return {
        ...prev,
        service_ids: filteredSelected
      };
    });
  }, [availableServicesToAdd]);

  const handleAddServiceableJob = async (e) => {
    e.preventDefault();
    if (!serviceableJobFormData.service_ids.length) {
      toast.error('Please select at least one service');
      return;
    }
    if (!selectedCityValues.length) {
      toast.error('Please select at least one city');
      return;
    }

    try {
      const jobs = [];
      serviceableJobFormData.service_ids.forEach((serviceId) => {
        selectedCityValues.forEach((city) => {
          jobs.push({
            service_id: serviceId,
            pincode: city
          });
        });
      });

      const response = jobs.length === 1
        ? await addContractorServiceableJob(id, jobs[0])
        : await addContractorServiceableJobsBulk(id, jobs);

      if (response.status === 'success') {
        toast.success(response.message || 'Serviceable jobs added successfully');
        setShowServiceableJobModal(false);
        setServiceableJobFormData({ category_ids: [], city_values: [], service_ids: [] });
        setCategorySearchTerm('');
        setServiceSearchTerm('');
        setCitySearchTerm('');
        await fetchServiceableJobs();
      } else {
        toast.error(response.message || 'Failed to add serviceable jobs');
      }
    } catch (error) {
      toast.error('Failed to add serviceable jobs');
    }
  };
  const handleServiceSelection = (serviceId, checked) => {
    setServiceableJobFormData((prev) => {
      const existing = new Set(prev.service_ids);
      if (checked) {
        existing.add(serviceId);
      } else {
        existing.delete(serviceId);
      }
      return {
        ...prev,
        service_ids: Array.from(existing)
      };
    });
  };

  const handleSelectAllAvailableServices = (checked) => {
    setServiceableJobFormData((prev) => ({
      ...prev,
      service_ids: checked ? availableServicesToAdd.map((service) => service.service_id) : []
    }));
  };

  const handleCitySelection = (cityName, checked) => {
    const selectedCity = String(cityName || '');
    setServiceableJobFormData((prev) => {
      const existing = new Set(prev.city_values);
      if (checked) {
        existing.add(selectedCity);
      } else {
        existing.delete(selectedCity);
      }
      return {
        ...prev,
        city_values: Array.from(existing)
      };
    });
  };

  const handleSelectAllFilteredCities = (checked) => {
    setServiceableJobFormData((prev) => ({
      ...prev,
      city_values: checked ? availableCitiesToAdd.map((city) => String(city.city || '')) : []
    }));
  };

  const handleCategorySelection = (categoryId, checked) => {
    const normalizedCategoryId = normalizeId(categoryId);
    if (!normalizedCategoryId) return;
    setServiceableJobFormData((prev) => {
      const existing = new Set(prev.category_ids);
      if (checked) {
        existing.add(normalizedCategoryId);
      } else {
        existing.delete(normalizedCategoryId);
      }
      return {
        ...prev,
        category_ids: Array.from(existing)
      };
    });
  };

  const handleSelectAllFilteredCategories = (checked) => {
    setServiceableJobFormData((prev) => ({
      ...prev,
      category_ids: checked
        ? filteredCategories.map((category) => getCategoryIdValue(category)).filter(Boolean)
        : []
    }));
  };


  const handleDeleteServiceableJob = async (jobId) => {
    try {
      const response = await deleteContractorServiceableJob(id, jobId);
      if (response.status === 'success') {
        toast.success(response.message || 'Serviceable job deleted successfully');
        await fetchServiceableJobs();
      } else {
        toast.error(response.message || 'Failed to delete serviceable job');
      }
    } catch (error) {
      toast.error('Failed to delete serviceable job');
    }
  };

  const toggleServiceGroup = (serviceId) => {
    setExpandedServiceGroups((prev) => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  const groupedByPincode = useMemo(() => {
    const groups = {};
    serviceableJobs.forEach((job) => {
      const pincodeKey = normalizePincode(job.pincode) || 'UNKNOWN';
      if (!groups[pincodeKey]) {
        groups[pincodeKey] = {
          pincode: pincodeKey,
          jobs: []
        };
      }
      groups[pincodeKey].jobs.push(job);
    });

    return Object.values(groups)
      .map((group) => ({
        ...group,
        jobs: [...group.jobs].sort((a, b) => {
          const aTitle = a.service?.title || '';
          const bTitle = b.service?.title || '';
          return aTitle.localeCompare(bTitle);
        })
      }))
      .sort((a, b) => a.pincode.localeCompare(b.pincode));
  }, [serviceableJobs]);

  const togglePincodeGroup = (pincode) => {
    setExpandedPincodeGroups((prev) => ({
      ...prev,
      [pincode]: !prev[pincode]
    }));
  };

  const renderLoadingState = (message) => (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        <p className="mt-4 text-gray-600">{message}</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Topbar />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold">Contractor Details</h1>
              <button
                onClick={() => navigate('/contractors')}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Back to Contractors
              </button>
            </div>
            {renderLoadingState('Loading contractor details...')}
          </div>
        </div>
      </div>
    );
  }

  if (!contractor) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Topbar />
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Contractor Details</h1>
            <div className="flex space-x-4">
              <button
                onClick={handleEdit}
                className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
              >
                Edit
              </button>
              <button
                onClick={() => navigate('/contractors')}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Back to Contractors
              </button>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex items-center space-x-4">
              {contractor.user.profile_photo && (
                <img
                  src={contractor.user.profile_photo}
                  alt={contractor.user.name}
                  className="w-20 h-20 rounded-full"
                />
              )}
              <div>
                <h2 className="text-xl font-semibold">{contractor.user.name}</h2>
                <p className="text-gray-600">{contractor.user.email}</p>
                <p className="text-gray-600">{contractor.user.mobile}</p>
              </div>
            </div>
          </div>

          <div className="border-b border-gray-200 mb-6">
            <nav className="flex space-x-4">
              <button
                onClick={() => handleTabChange('details')}
                className={`px-4 py-2 ${
                  activeTab === 'details'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => handleTabChange('workers')}
                className={`px-4 py-2 ${
                  activeTab === 'workers'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Workers
              </button>
              <button
                onClick={() => handleTabChange('orders')}
                className={`px-4 py-2 ${
                  activeTab === 'orders'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Orders
              </button>
              <button
                onClick={() => handleTabChange('serviceable-jobs')}
                className={`px-4 py-2 ${
                  activeTab === 'serviceable-jobs'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Serviceable Jobs
              </button>
            </nav>
          </div>

          {activeTab === 'details' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Personal Information</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b pb-2">
                    <span className="font-medium text-gray-600">Name</span>
                    <span className="text-gray-800">{contractor.user.name}</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-2">
                    <span className="font-medium text-gray-600">Email</span>
                    <span className="text-gray-800">{contractor.user.email}</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-2">
                    <span className="font-medium text-gray-600">Mobile</span>
                    <span className="text-gray-800">{contractor.user.mobile}</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-2">
                    <span className="font-medium text-gray-600">Gender</span>
                    <span className="text-gray-800">{contractor.user.gender}</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-2">
                    <span className="font-medium text-gray-600">Age</span>
                    <span className="text-gray-800">{contractor.user.age}</span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Contractor Information</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b pb-2">
                    <span className="font-medium text-gray-600">Status</span>
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      contractor.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {contractor.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-2">
                    <span className="font-medium text-gray-600">Commission Percentage</span>
                    <span className="text-gray-800">{Number(contractor.commission_percentage).toFixed(2)}%</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'workers' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Workers</h3>
              {loadingWorkers ? (
                renderLoadingState('Loading workers...')
              ) : workers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr>
                        <th className="py-2 px-4 border-b">Name</th>
                        <th className="py-2 px-4 border-b">Email</th>
                        <th className="py-2 px-4 border-b">Mobile</th>
                        <th className="py-2 px-4 border-b">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workers.map((worker) => (
                        <tr key={worker.worker_id}>
                          <td className="py-2 px-4 border-b">{worker.user.name}</td>
                          <td className="py-2 px-4 border-b">{worker.user.email}</td>
                          <td className="py-2 px-4 border-b">{worker.user.mobile}</td>
                          <td className="py-2 px-4 border-b">
                            <span className={`px-2 py-1 rounded ${
                              worker.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {worker.active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">No workers found</p>
              )}
            </div>
          )}

          {activeTab === 'orders' && (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Orders</h3>
                <div className="flex justify-end mb-4">
                  <select
                    value={orderStatus}
                    onChange={handleOrderStatusChange}
                    className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Status</option>
                    <option value="ACCEPTED">Accepted</option>
                    <option value="ON_THE_WAY">On the Way</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="DONE">Done</option>
                    <option value="COMPLETE">Complete</option>
                    <option value="CANCELED">Canceled</option>
                  </select>
                </div>
              </div>
              {loadingOrders ? (
                renderLoadingState('Loading orders...')
              ) : orders.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr>
                        <th className="py-2 px-4 border-b">Order ID</th>
                        <th className="py-2 px-4 border-b">Status</th>
                        <th className="py-2 px-4 border-b">Amount</th>
                        <th className="py-2 px-4 border-b">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order.order_id}>
                          <td className="py-2 px-4 border-b">{order.order_id}</td>
                          <td className="py-2 px-4 border-b">
                            <span className={`px-2 py-1 rounded ${
                              order.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                              order.status === 'CANCELED' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {order.status}
                            </span>
                          </td>
                          <td className="py-2 px-4 border-b">${order.amount}</td>
                          <td className="py-2 px-4 border-b">{formatEasternDate(order.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">No orders found</p>
              )}
            </div>
          )}

          {activeTab === 'serviceable-jobs' && (
            <div>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold">Serviceable Jobs</h3>
                <div className="flex items-center gap-2">
                  <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setGroupByMode('service')}
                      className={`px-3 py-2 text-sm ${
                        groupByMode === 'service'
                          ? 'bg-blue-500 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Group by Service
                    </button>
                    <button
                      type="button"
                      onClick={() => setGroupByMode('pincode')}
                      className={`px-3 py-2 text-sm border-l border-gray-300 ${
                        groupByMode === 'pincode'
                          ? 'bg-blue-500 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Group by City/Pincode
                    </button>
                  </div>
                  <button
                    onClick={async () => {
                      setLoadingAddServiceableJobMeta(true);
                      try {
                        await Promise.all([
                          fetchCategories(),
                          fetchServices(),
                          fetchServiceableCities()
                        ]);
                        setActiveAddStep('category');
                        setShowServiceableJobModal(true);
                      } finally {
                        setLoadingAddServiceableJobMeta(false);
                      }
                    }}
                    disabled={loadingAddServiceableJobMeta}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loadingAddServiceableJobMeta ? 'Loading...' : 'Add Serviceable Job'}
                  </button>
                </div>
              </div>

              {loadingServiceableJobs ? (
                renderLoadingState('Loading serviceable jobs...')
              ) : groupByMode === 'service' && groupedServiceableJobs.length > 0 ? (
                <div className="space-y-3">
                  {groupedServiceableJobs.map((group) => {
                    const isExpanded = Boolean(expandedServiceGroups[group.serviceId]);
                    return (
                      <div key={group.serviceId} className="border rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => toggleServiceGroup(group.serviceId)}
                          className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left"
                        >
                          <div>
                            <p className="font-semibold text-gray-900">{group.serviceTitle}</p>
                            <p className="text-sm text-gray-600">
                              {group.categoryName} {group.subCategoryName !== '-' ? `• ${group.subCategoryName}` : ''}
                            </p>
                          </div>
                          <div className="text-sm font-medium text-gray-700">
                            {group.jobs.length} {group.jobs.length === 1 ? 'City' : 'Cities'} {isExpanded ? '▲' : '▼'}
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="overflow-x-auto">
                            <table className="min-w-full bg-white">
                              <thead>
                                <tr>
                                  <th className="py-2 px-4 border-b text-left">City / Pincode</th>
                                  <th className="py-2 px-4 border-b text-left">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.jobs.map((job) => (
                                  <tr key={job.id}>
                                    <td className="py-2 px-4 border-b">{job.pincode}</td>
                                    <td className="py-2 px-4 border-b">
                                      <button
                                        onClick={() => handleDeleteServiceableJob(job.id)}
                                        className="text-red-600 hover:text-red-800"
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : groupByMode === 'pincode' && groupedByPincode.length > 0 ? (
                <div className="space-y-3">
                  {groupedByPincode.map((group) => {
                    const isExpanded = Boolean(expandedPincodeGroups[group.pincode]);
                    return (
                      <div key={group.pincode} className="border rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => togglePincodeGroup(group.pincode)}
                          className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left"
                        >
                          <div>
                            <p className="font-semibold text-gray-900">{group.pincode}</p>
                            <p className="text-sm text-gray-600">City/Pincode</p>
                          </div>
                          <div className="text-sm font-medium text-gray-700">
                            {group.jobs.length} {group.jobs.length === 1 ? 'Service' : 'Services'} {isExpanded ? '▲' : '▼'}
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="overflow-x-auto">
                            <table className="min-w-full bg-white">
                              <thead>
                                <tr>
                                  <th className="py-2 px-4 border-b text-left">Service</th>
                                  <th className="py-2 px-4 border-b text-left">Category</th>
                                  <th className="py-2 px-4 border-b text-left">Sub Category</th>
                                  <th className="py-2 px-4 border-b text-left">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.jobs.map((job) => (
                                  <tr key={job.id}>
                                    <td className="py-2 px-4 border-b">{job.service?.title || '-'}</td>
                                    <td className="py-2 px-4 border-b">{job.category?.name || job.category?.title || '-'}</td>
                                    <td className="py-2 px-4 border-b">{job.subCategory?.name || job.subCategory?.title || '-'}</td>
                                    <td className="py-2 px-4 border-b">
                                      <button
                                        onClick={() => handleDeleteServiceableJob(job.id)}
                                        className="text-red-600 hover:text-red-800"
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">No serviceable jobs found</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Serviceable Job Modal */}
      {showServiceableJobModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Add Serviceable Job</h2>
            <form onSubmit={handleAddServiceableJob}>
              <div className="space-y-4">
                <div className="border rounded-md">
                  <button
                    type="button"
                    onClick={() => setActiveAddStep('category')}
                    className="w-full px-3 py-2 flex items-center justify-between bg-gray-50 hover:bg-gray-100"
                  >
                    <span className="font-medium text-sm text-gray-800">1. Categories (optional)</span>
                    <span className="text-xs text-gray-600">
                      {serviceableJobFormData.category_ids.length} selected {activeAddStep === 'category' ? '▲' : '▼'}
                    </span>
                  </button>
                  {activeAddStep === 'category' && (
                    <div className="p-3">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700">Categories</label>
                        {filteredCategories.length > 0 && (
                          <label className="text-xs text-gray-600 flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={
                                filteredCategories.length > 0 &&
                                filteredCategories.every((category) =>
                                  serviceableJobFormData.category_ids.includes(getCategoryIdValue(category))
                                )
                              }
                              onChange={(e) => handleSelectAllFilteredCategories(e.target.checked)}
                            />
                            Select all
                          </label>
                        )}
                      </div>
                      <input
                        type="text"
                        value={categorySearchTerm}
                        onChange={(e) => setCategorySearchTerm(e.target.value)}
                        className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Search categories"
                      />
                      <div className="mt-2 max-h-36 overflow-y-auto border border-gray-200 rounded-md p-2">
                        {filteredCategories.length === 0 ? (
                          <p className="text-sm text-gray-500">No categories found</p>
                        ) : (
                          filteredCategories.map((category) => {
                            const categoryIdValue = getCategoryIdValue(category);
                            return (
                            <label key={categoryIdValue || category.title || category.name} className="flex items-center gap-2 py-1 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={serviceableJobFormData.category_ids.includes(categoryIdValue)}
                                onChange={(e) => handleCategorySelection(categoryIdValue, e.target.checked)}
                              />
                              {category.title || category.name}
                            </label>
                            );
                          })
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        If none selected, services from all categories are shown.
                      </p>
                      <div className="mt-3 flex justify-end">
                        <button
                          type="button"
                          onClick={() => setActiveAddStep('service')}
                          className="text-sm bg-blue-500 text-white px-3 py-1.5 rounded hover:bg-blue-600"
                        >
                          Next: Services
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border rounded-md">
                  <button
                    type="button"
                    onClick={() => setActiveAddStep('service')}
                    className="w-full px-3 py-2 flex items-center justify-between bg-gray-50 hover:bg-gray-100"
                  >
                    <span className="font-medium text-sm text-gray-800">2. Services</span>
                    <span className="text-xs text-gray-600">
                      {serviceableJobFormData.service_ids.length} selected {activeAddStep === 'service' ? '▲' : '▼'}
                    </span>
                  </button>
                  {activeAddStep === 'service' && (
                    <div className="p-3">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700">Services</label>
                        {availableServicesToAdd.length > 0 && (
                          <label className="text-xs text-gray-600 flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={
                                availableServicesToAdd.length > 0 &&
                                availableServicesToAdd.every((service) =>
                                  serviceableJobFormData.service_ids.includes(service.service_id)
                                )
                              }
                              onChange={(e) => handleSelectAllAvailableServices(e.target.checked)}
                            />
                            Select all
                          </label>
                        )}
                      </div>
                      <input
                        type="text"
                        value={serviceSearchTerm}
                        onChange={(e) => setServiceSearchTerm(e.target.value)}
                        className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Search services"
                      />
                      <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2">
                        {availableServicesToAdd.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            {selectedCityValues.length
                              ? 'All services are already mapped for selected cities'
                              : 'No services found for selected filters'}
                          </p>
                        ) : (
                          availableServicesToAdd.map((service) => (
                            <label key={service.service_id} className="flex items-center gap-2 py-1 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={serviceableJobFormData.service_ids.includes(service.service_id)}
                                onChange={(e) => handleServiceSelection(service.service_id, e.target.checked)}
                              />
                              {service.title}
                            </label>
                          ))
                        )}
                      </div>
                      <div className="mt-3 flex justify-end">
                        <button
                          type="button"
                          onClick={() => setActiveAddStep('city')}
                          className="text-sm bg-blue-500 text-white px-3 py-1.5 rounded hover:bg-blue-600"
                        >
                          Next: Cities
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border rounded-md">
                  <button
                    type="button"
                    onClick={() => setActiveAddStep('city')}
                    className="w-full px-3 py-2 flex items-center justify-between bg-gray-50 hover:bg-gray-100"
                  >
                    <span className="font-medium text-sm text-gray-800">3. Cities</span>
                    <span className="text-xs text-gray-600">
                      {serviceableJobFormData.city_values.length} selected {activeAddStep === 'city' ? '▲' : '▼'}
                    </span>
                  </button>
                  {activeAddStep === 'city' && (
                    <div className="p-3">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700">Cities</label>
                    {availableCitiesToAdd.length > 0 && (
                          <label className="text-xs text-gray-600 flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={
                            availableCitiesToAdd.length > 0 &&
                            availableCitiesToAdd.every((city) =>
                                  serviceableJobFormData.city_values.includes(String(city.city || ''))
                                )
                              }
                              onChange={(e) => handleSelectAllFilteredCities(e.target.checked)}
                            />
                            Select all
                          </label>
                        )}
                      </div>
                      <input
                        type="text"
                        value={citySearchTerm}
                        onChange={(e) => setCitySearchTerm(e.target.value)}
                        className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Search cities"
                      />
                      <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2">
                    {availableCitiesToAdd.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        {serviceableJobFormData.service_ids.length
                          ? 'All selected services are already mapped for searched cities'
                          : 'No cities found'}
                      </p>
                        ) : (
                      availableCitiesToAdd.map((city) => {
                            const cityValue = String(city.city || '');
                            return (
                              <label key={city.id} className="flex items-center gap-2 py-1 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={serviceableJobFormData.city_values.includes(cityValue)}
                                  onChange={(e) => handleCitySelection(city.city, e.target.checked)}
                                />
                                {city.city}
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                  </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowServiceableJobModal(false);
                    setServiceableJobFormData({ category_ids: [], city_values: [], service_ids: [] });
                    setActiveAddStep('category');
                    setCategorySearchTerm('');
                    setServiceSearchTerm('');
                    setCitySearchTerm('');
                  }}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!serviceableJobFormData.service_ids.length || !serviceableJobFormData.city_values.length}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Edit Contractor</h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Mobile</label>
                  <input
                    type="text"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Age</label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Commission Percentage</label>
                  <input
                    type="number"
                    name="commission_percentage"
                    value={formData.commission_percentage}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    name="active"
                    value={formData.active}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}

export default ContractorDetails; 
